import axios, { AxiosInstance } from "axios";
import { decodeToken } from "../utils/jwt";

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  role: string;
  access_token: string;
  refresh_token: string;
}

class AuthService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      this.logout();
      throw new Error("No refresh token available");
    }

    try {
      const response = await this.api.post("/auth/refresh-token", {
        refresh_token: refreshToken,
      });

      const { access_token } = response.data;

      if (!access_token) {
        throw new Error("No access token in response");
      }

      localStorage.setItem("access_token", access_token);
      return access_token;
    } catch {
      this.logout();
      throw new Error("Failed to refresh token");
    }
  }

  logout(): void {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.href = "/login";
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.api.post("/auth/admin-login", credentials);
      console.log("Login response:", response.data);

      const { access_token, refresh_token, role } = response.data;
      console.log("role", role);

      if (!access_token || !refresh_token) {
        throw new Error("Missing tokens in login response");
      }

      const decodedToken = decodeToken(access_token);
      if (
        !decodedToken ||
        !["admin", "superadmin"].includes(decodedToken.role)
      ) {
        throw new Error("Unauthorized: Insufficient permissions");
      }

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("role", role);

      return { access_token, refresh_token, role };
    } catch (error: any) {
      console.error("Login failed:", error.response?.data || error.message);
      throw new Error(error.response?.data?.message || "Login failed.");
    }
  }

  getAccessToken(): string | null {
    return localStorage.getItem("access_token");
  }

  getUserRole(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const decoded = decodeToken(token);
    return decoded?.role || null;
  }

  isAuthorized(): boolean {
    const role = this.getUserRole();
    return role === "admin" || role === "superadmin";
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken() && this.isAuthorized();
  }
}

const authService = new AuthService();
export default authService;
